/* eslint-disable sonarjs/cognitive-complexity */
import debounce from 'lodash/debounce';
import Link from 'next/link';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { BILLING_ADDRESS_AUTOCOMPLETE } from '../../brands/default/content/components/address';
import {
  AUTO_ADDRESS_LOOKUP,
  EIR_POSTCODE,
  TEXT_LOWERCASE,
} from '../../brands/default/features';
import { autocomplete, selectAddressByAddressKey } from '../../helpers/address';
import { trackEvent } from '../../helpers/analytics';
import ThemeContext from '../../providers/ThemeProvider';
import Message from '../_generic/Message';
import FormikInput from '../_generic/input/FormikInput';
import useToast from '../_generic/toast/UseToast';
import AddressFields from './AddressFields';
import ListOfAddressesAutocomplete from './ListOfAddressesAutocomplete';
import LookupAddressAutocomplete from './LookupAddressAutocomplete';

const BillingAddressAutocomplete = ({
  disabled = false,
  errors = {},
  extraClasses = '',
  isRegistrationPage = false,
  isShowError = false,
  lookupAddressText = '',
  onBlurFunction,
  onChangeFunction,
  onManualAddress = () => {},
  setValues,
  showAllFields = false,
  touched = {},
  values = {},
}) => {
  const theme = useContext(ThemeContext);
  const [showAddressFields, setShowAddressFields] = useState(showAllFields);
  const [fetchedAddresses, setFetchedAddresses] = useState();
  const toast = useToast();
  const [userPostcode] = useSelector((state) => [
    state?.user?.userprofile?.postcode,
  ]);
  const classes = `mobile-highlight-border ${extraClasses} ${
    TEXT_LOWERCASE && 'lowercase_placeholder'
  }`;

  const {
    enterAddressManually,
    lookupOrEnterAddress,
    registerAddressDoesntExist,
    registrationEirCode,
    registrationLookupError,
  } = BILLING_ADDRESS_AUTOCOMPLETE;

  useEffect(() => {
    if (!AUTO_ADDRESS_LOOKUP) {
      setShowAddressFields(true);
      onManualAddress(true);
    }
  }, [onManualAddress]);

  const handleBlur = (event) => {
    const name = event.target.name;
    if (name === 'addressLine1') trackEvent('address_line1_field');
    if (name === 'city') trackEvent('address_city_field');
    if (name === 'addressLine2') trackEvent('address_line2_field');

    if (name === 'postCode') {
      if (EIR_POSTCODE) {
        trackEvent('eircode_field');
      } else {
        trackEvent('postcode_field');
      }
    }

    onBlurFunction(event);
  };

  const manualAddress = (event) => {
    // keyboard - only trigger with enter key
    if (event && event.key && event.key !== 'Enter') return;

    event.preventDefault();
    setShowAddressFields(true);
    onManualAddress(true);
  };

  const lookupAddress = async (lookupValue) => {
    const response = await autocomplete(lookupValue);

    if (!response || response.status !== (200 || 202)) {
      setFetchedAddresses(registerAddressDoesntExist);
      setShowAddressFields(true);
      onManualAddress(true);

      return;
    }

    const addressReferences = response?.data?.data?.response;

    if (addressReferences.length > 0) {
      setFetchedAddresses(addressReferences);
    } else {
      setFetchedAddresses(registerAddressDoesntExist);
    }
  };

  const selectAddress = async (addressKey) => {
    const response = await selectAddressByAddressKey(addressKey);

    if (!response || response.status !== (200 || 202)) {
      toast.addToast(registrationLookupError, 'toast-error');
      setFetchedAddresses();
      setShowAddressFields(true);
      onManualAddress(true);

      return;
    }

    const selectedAddress = response.data.data.result.address;

    if (selectedAddress) {
      const newValues = {
        ...values,
        addressLine1: selectedAddress.address_line_1,
        addressLine2: selectedAddress.address_line_2,
        city: selectedAddress.locality,
        postCode: selectedAddress.postal_code,
      };
      // eslint-disable-next-line fp/no-delete
      delete newValues.autocomplete;
      setValues(newValues);
    } else {
      toast.addToast(registrationLookupError, 'toast-error');
    }
    setFetchedAddresses();
    setShowAddressFields(true);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const lookupAddressDebounced = useCallback(
    debounce((lookup) => {
      // look up using api if more than 3 characters
      if (lookup && lookup.length > 3) {
        lookupAddress(lookup);
      } else {
        setFetchedAddresses();
      }
    }, 400),
    [],
  );

  const handleChange = (event) => {
    const lookup = event?.target?.value;
    // debounce so only lookup after short pause
    lookupAddressDebounced(lookup);

    onChangeFunction(event);
  };

  useEffect(() => {
    // auto populate suggestions if already has a postcode, not LSIE
    if (AUTO_ADDRESS_LOOKUP && userPostcode) {
      if (setValues) {
        setValues({
          ...values,
          autocomplete: userPostcode,
        });
      }
      lookupAddressDebounced(userPostcode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPostcode, lookupAddressDebounced]);

  return (
    <>
      {AUTO_ADDRESS_LOOKUP && (
        <LookupAddressAutocomplete
          disabled={disabled}
          errors={errors}
          extraClasses={classes}
          lookupAddress={lookupAddress}
          lookupAddressText={lookupAddressText}
          onBlurFunction={handleBlur}
          onChangeFunction={handleChange}
          touched={touched}
          values={values}
        />
      )}
      {Boolean(fetchedAddresses) && (
        <div className="col-12">
          <ListOfAddressesAutocomplete
            addressList={fetchedAddresses}
            selectAddress={selectAddress}
          />
        </div>
      )}
      {isShowError && !showAddressFields && (
        <div className="col-12">
          <Message
            message={lookupOrEnterAddress}
            showIcon={false}
            type="ERROR"
          />
        </div>
      )}
      {AUTO_ADDRESS_LOOKUP && !showAddressFields && (
        <div className="col-12 input-container text-right">
          <Link href="#">
            <a
              className="manual-address"
              onClick={manualAddress}
              onKeyDown={manualAddress}
              role="link"
              tabIndex={0}
            >
              {enterAddressManually}
            </a>
          </Link>
        </div>
      )}
      {showAddressFields && (
        <AddressFields
          componentClass={classes}
          disabled={disabled}
          errors={errors}
          isShowPostcode={AUTO_ADDRESS_LOOKUP}
          onBlurFunction={handleBlur}
          onChangeFunction={onChangeFunction}
          touched={touched}
          values={values}
        />
      )}
      {!AUTO_ADDRESS_LOOKUP && showAddressFields && (
        <div className="col-12">
          <FormikInput
            disabled={disabled}
            extraClasses={classes}
            inputTouched={touched.postCode}
            inputValue={values.postCode}
            name={'postCode'}
            onBlurFunction={handleBlur}
            onChangeFunction={onChangeFunction}
            placeholder={`${registrationEirCode}${
              isRegistrationPage ? '' : '*'
            }`}
            required={!isRegistrationPage}
            type="text"
          />
        </div>
      )}
      <style jsx>{`
        a.manual-address {
          color: ${theme.colors.primary};
        }
      `}</style>
    </>
  );
};

BillingAddressAutocomplete.propTypes = {
  disabled: PropTypes.bool,
  errors: PropTypes.object.isRequired,
  extraClasses: PropTypes.string,
  isRegistrationPage: PropTypes.bool,
  isShowError: PropTypes.bool,
  lookupAddressText: PropTypes.string,
  onBlurFunction: PropTypes.func.isRequired,
  onChangeFunction: PropTypes.func.isRequired,
  onManualAddress: PropTypes.func,
  setValues: PropTypes.func.isRequired,
  showAllFields: PropTypes.bool,
  touched: PropTypes.object.isRequired,
  values: PropTypes.object.isRequired,
};

export default BillingAddressAutocomplete;
